import { useCommonStore } from '~/common/stores/common.store';
import { getUserFullName } from '~/common/utils/common.utils.js';
import { ANNOTATION_TYPES, CUSTOM_ANNOTATION_TYPES, PDF_NET_CDN_URL, PDF_TRON_CDN_BASE_URL, PDF_WORKER_PATH, WEB_CORE_CDN_URL } from '~/plans/constants';

/**
 * Creates an array containing 1…N
 * @param {number} count
 * @returns {number[]}
 */
export function createArrayFromCount(count) {
  return Array.from({ length: count }, (_, i) => i + 1);
}

/**
 * Download blob data to file
 * @param {Blob} blob File blob
 * @param {string} file_extension
 * @param {string} file_name
 * @returns
 */
export function downloadBlob(blob, file_extension, file_name = 'file') {
  // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
  const blobUrl = URL.createObjectURL(blob);

  // Create a link element
  const link = document.createElement('a');

  // Set link's href to point to the Blob URL
  link.href = blobUrl;
  link.download = `${file_name}.${file_extension}`;

  // Append link to the body
  document.body.appendChild(link);

  // Dispatch click event on the link
  // This is necessary as link.click() does not work on the latest firefox
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    }),
  );

  // Remove link from body
  document.body.removeChild(link);
}

/**
 * Parse the XML string to get the specified attribute value
 * @param {string} xml_string
 * @param {string} attr_name
 * @returns {}
 */
export function extractAttrFromXML(xml_string, attr_name) {
  const parser = new DOMParser();
  const xml_doc = parser.parseFromString(xml_string, 'text/xml');
  const annot_elem = xml_doc.querySelector(`[${attr_name}]`);

  return annot_elem?.getAttribute(attr_name);
}

/**
 * Fullscreen mode toggler
 * @param {DOMElement} element
 */
export function toggleFullscreen(element) {
  const is_fullscreen_mode = element.getAttribute('fullscreen');
  if (is_fullscreen_mode) {
    element.removeAttribute('fullscreen');
    document.exitFullscreen();
  }
  else {
    element.setAttribute('fullscreen', true);

    const fullscreenMethod
    = element.requestFullScreen
    || element.webkitRequestFullScreen
    || element.mozRequestFullScreen
    || element.msRequestFullScreen;

    fullscreenMethod?.call(element);
  }
}

export function getImageUrl(path, name, extension = 'svg') {
  return new URL(`${path}/${name}.${extension}`, import.meta.url).href;
}

export function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
}

export async function initiateStandalonePDFTron() {
  if (window.Core)
    return;

  await import(/* @vite-ignore */ WEB_CORE_CDN_URL);
  await import(/* @vite-ignore */ PDF_NET_CDN_URL);

  window.Core.setWorkerPath(PDF_TRON_CDN_BASE_URL);
  window.Core.setPDFWorkerPath(PDF_WORKER_PATH);
  window.Core.disableEmbeddedJavaScript(); // disabling pdf javascript
}

/**
 * Combine multiple XFDF annotation data into a single XFDF string
 * @param {Array} items Array of objects containing annotation_data (each is full XFDF XML)
 * @param {(annotationNode: Element, item: object) => void} [transformAnnotation] Optional callback invoked for each annotation element before it is added
 * to the combined XFDF. Can be used to modify attributes such as `title`, `subject`, etc.
 * @returns {string} Combined XFDF XML string
 */
export function combineXFDFAnnotations(items = [], transformAnnotation) {
  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  const xmls = [];

  items.forEach((obj) => {
    if (!obj.annotation_data)
      return;

    const xmlDoc = parser.parseFromString(obj.annotation_data, 'text/xml');
    const annotsNode = xmlDoc.getElementsByTagName('annots')[0];

    if (!annotsNode)
      return;

    Array.from(annotsNode.children).forEach((node) => {
      transformAnnotation?.(node, obj);
      xmls.push(node);
    });
  });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">',
    '<annots>',
    ...xmls.map(node => serializer.serializeToString(node)),
    '</annots>',
    '</xfdf>',
  ].join('\n');
}

async function applyXFDF(pdfDoc, annotations, options = {}) {
  const { flatten_annotations = false } = options;
  const xfdfString = combineXFDFAnnotations(annotations, (node, obj) => {
    const common_store = useCommonStore();
    const organization_user = common_store.get_user(obj.owner);
    const internal_user = common_store?.internal_users_map?.[obj.owner];
    const user_name = getUserFullName(organization_user || internal_user || null);
    node.setAttribute('title', user_name);

    const annotation_type = node.getAttribute('subject');
    if ([CUSTOM_ANNOTATION_TYPES.COMMENT, ANNOTATION_TYPES.NOTE].includes(annotation_type)) {
      const icon = node.getAttribute('icon');
      icon === 'Draft' && (node.setAttribute('icon', 'Comment'));
    }
  });

  await pdfDoc.mergeXFDFString(xfdfString);

  // flatten all annotations in the document
  if (flatten_annotations) {
    await pdfDoc.flattenAnnotations();
  }
}

export async function getPDFWithAnnotations(file_url, annotations, options = {}) {
  const { flatten_annotations = false } = options;
  await window.Core.PDFNet.initialize(import.meta.env.VITE_APP_PDF_TRON_LICENSE_KEY);

  const bytes = await fetch(file_url).then(r => r.arrayBuffer());
  const is_pdf_file_regex = /\.pdf$/i;

  let pdfDoc;

  if (is_pdf_file_regex.test(file_url)) {
    // Load PDF
    pdfDoc = await window.Core.PDFNet.PDFDoc.createFromBuffer(bytes);
  }
  else {
    throw new Error('Unsupported file type. Only PDF allowed.');
  }

  pdfDoc.initSecurityHandler();
  pdfDoc.lock();

  // Merge XFDF annotations
  await applyXFDF(pdfDoc, annotations, { flatten_annotations });

  // Save & export
  const outBytes = await pdfDoc.saveMemoryBuffer(
    window.Core.PDFNet.SDFDoc.SaveOptions.e_linearized,
  );

  return outBytes;
}

export async function downloadAnnotatedPDF(file_url, annotations, download_file_name, options = {}) {
  const { flatten_annotations = false } = options;
  const pdf_bytes = await getPDFWithAnnotations(file_url, annotations, { flatten_annotations });
  const blob = new Blob([pdf_bytes], { type: 'application/pdf' });
  downloadBlob(blob, 'pdf', download_file_name);
}
